import { FC, useEffect, useMemo, useState } from 'react';
import {
  AutomaticPromotion,
  fetchAutomaticPromotion,
} from '../../api/automaticPromotions';
import PageHeaderWithBack from '../../components/PageHeaderWithBack';

interface PromotionsDetailPanelProps {
  id: string;
  onBack: () => void;
}

const formatBoolean = (value: boolean) => (value ? 'Yes' : 'No');

const PromotionsDetailPanel: FC<PromotionsDetailPanelProps> = ({ id, onBack }) => {
  const [data, setData] = useState<AutomaticPromotion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchAutomaticPromotion(id)
      .then((response) => {
        if (isMounted) {
          setData(response);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load promotion.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [id]);

  const promotionType = useMemo(() => {
    if (!data) {
      return '—';
    }
    return data.promotionTypeKey ?? data.promotionType ?? '—';
  }, [data]);

  const locationNames = useMemo(() => {
    if (!data?.locations || data.locations.length === 0) {
      return ['No linked locations'];
    }
    return data.locations.map((item) => item?.name ?? `Location ${item?.id ?? ''}`);
  }, [data?.locations]);

  if (loading) {
    return (
      <div className="rounded-2xl bg-white px-6 py-5 text-sm text-slate-500 shadow-inner">
        Loading promotion...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-2xl bg-white px-6 py-5 text-sm text-danger shadow-inner">
        {error ?? 'Promotion not found.'}
        <div className="mt-4">
          <button
            type="button"
            className="rounded-xl bg-primary px-4 py-2 text-sm text-white shadow"
            onClick={onBack}
          >
            Back to promotions
          </button>
        </div>
      </div>
    );
  }

  const formatDateTime = (value: string | null) => {
    if (!value) {
      return '—';
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
  };

  const hasTiers = Array.isArray(data.promotionTiers) && data.promotionTiers.length > 0;

  return (
    <div className="rounded-2xl bg-white px-6 py-5 shadow-inner">
    
      <PageHeaderWithBack
        title={data.name}
        description={<p className="mt-1 text-sm text-slate-500">ERP ID {data.erpId}</p>}
        onBack={onBack}
        backLabel="Back to promotions"
      />

      <div className="mt-6 grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
          <h3 className="text-base font-semibold text-slate-700">Promotion Details</h3>
          <dl className="mt-4 space-y-3 text-sm text-slate-700">
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Type</dt>
              <dd>{promotionType}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Timeframe</dt>
              <dd>{data.timeframeType ?? '—'}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">POS Available</dt>
              <dd>{formatBoolean(data.isAvailableInPos)}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Membership Required</dt>
              <dd>{formatBoolean(data.isMembershipRequired)}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Automatic</dt>
              <dd>{formatBoolean(data.isAutomatic)}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Dream Price Applicable</dt>
              <dd>{formatBoolean(data.dreamPriceApplicable)}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Updated</dt>
              <dd>{formatDateTime(data.updatedAt)}</dd>
            </div>
          </dl>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
            <h3 className="text-base font-semibold text-slate-700">Locations</h3>
            <ul className="mt-3 space-y-2">
              {locationNames.map((name, index) => (
                <li
                  key={`${name}-${index}`}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600"
                >
                  {name}
                </li>
              ))}
            </ul>

            <div className="mt-6 border-t border-slate-200 pt-4">
              <h3 className="text-base font-semibold text-slate-700">Schedule</h3>
              <dl className="mt-3 space-y-2 text-sm text-slate-700">
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Start Date</dt>
                  <dd>{formatDateTime(data.startDate)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">End Date</dt>
                  <dd>{formatDateTime(data.endDate)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Start Time</dt>
                  <dd>{data.startTime ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">End Time</dt>
                  <dd>{data.endTime ?? '—'}</dd>
                </div>
              </dl>
            </div>
          </div>

          {hasTiers ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
              <h3 className="text-base font-semibold text-slate-700">Discount tiers</h3>
              <div className="mt-3 space-y-3 text-xs text-slate-700">
                {data.promotionTiers!.map((tier, index) => {
                  const knownKeys = new Set([
                    'minimum_spend_amount',
                    'flat_amount',
                    'percentage',
                  ]);
                  const otherEntries = Object.entries(
                    tier as Record<string, unknown>,
                  ).filter(([key, value]) => {
                    if (knownKeys.has(key)) return false;
                    if (value === null || value === undefined) return false;
                    if (value === '') return false;
                    return true;
                  });

                  const formatKey = (key: string) =>
                    key
                      .replace(/_/g, ' ')
                      .replace(/\s+/g, ' ')
                      .replace(/^\w/, (c) => c.toUpperCase());

                  const entries: { label: string; value: unknown }[] = [];
                  if ((tier as any).minimum_spend_amount !== null && (tier as any).minimum_spend_amount !== undefined) {
                    entries.push({
                      label: 'Min spend',
                      value: (tier as any).minimum_spend_amount,
                    });
                  }
                  if ((tier as any).flat_amount !== null && (tier as any).flat_amount !== undefined) {
                    entries.push({
                      label: 'Flat amount',
                      value: (tier as any).flat_amount,
                    });
                  }
                  if ((tier as any).percentage !== null && (tier as any).percentage !== undefined) {
                    entries.push({
                      label: 'Percentage',
                      value: (tier as any).percentage,
                    });
                  }

                  otherEntries.forEach(([key, value]) => {
                    entries.push({
                      label: formatKey(key),
                      value,
                    });
                  });

                  return (
                    <div
                      key={index}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2"
                    >
                      <p className="text-xs font-semibold text-slate-700">
                        Tier {index + 1}
                      </p>
                      <dl className="mt-1 space-y-1">
                        {entries.map((entry) => (
                          <div className="flex gap-2" key={entry.label}>
                            <dt className="w-32 text-[11px] font-semibold text-slate-600">
                              {entry.label}
                            </dt>
                            <dd className="text-slate-700">
                              {typeof entry.value === 'object'
                                ? JSON.stringify(entry.value)
                                : String(entry.value)}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PromotionsDetailPanel;
